import { FormikProps } from 'formik';
import { RefObject, useRef } from 'react';
import { useMutation } from '../../../request/hooks/useMutation';
import { PipelineAPI } from '../../api/PipelineAPI';
import { PipelineSettingsTrackers } from '../../tracking/pipeline-settings-trackers';
import { useAnalyticsTracker } from '../../../tracking/hooks/useAnalyticsTracker';
import { Integration, RawIntegration } from '../../../shared/interfaces/integration';
import { useAsyncValidationForm } from '../../../shared/components/AsyncValidationForm/hooks/useAsyncValidationForm';
import {
  PipelineConfigPayload,
  PipelineSettingsAsyncFields,
  PipelineSettingsFormState
} from '../interfaces/pipeline-settings-form-state';
import { PipelineSettingsConfigHelper } from '../pipelineSettingsConfigHelper';
import { useScrollToBottom } from '../../../shared/hooks/useScrollToBottom';
import { usePipelineSettingsValidations } from './usePipelineSettingsValidations';
import { PipelineSettingsFeaturesData } from '../../interfaces/pipeline-settings-config';
import { isEditPipelineConfigAllowed } from '../../../shared/utils/config';
import { MutationState } from '../../../request/interfaces/entity-ui-state';

export function usePipelineSettingsForm({
  pipeline,
  onSavePipeline,
  formContainerRef,
  formikRef,
  features,
  defaultPipelineName,
  isEditing
}: {
  pipeline: Integration;
  onSavePipeline: (rawPipeline: RawIntegration) => void;
  formContainerRef: RefObject<HTMLDivElement>;
  formikRef: RefObject<FormikProps<PipelineSettingsFormState>>;
  features: PipelineSettingsFeaturesData;
  defaultPipelineName: string;
  isEditing: boolean;
}) {
  const { eventTrack } = useAnalyticsTracker();
  const { scrollToBottom } = useScrollToBottom(formContainerRef);
  const validationSchema = usePipelineSettingsValidations(pipeline, features);

  const initialFormValues = useRef<PipelineSettingsFormState>(
    PipelineSettingsConfigHelper.getInitialValues(features, pipeline, defaultPipelineName)
  );

  const { runValidationsAndSubmit, isValidating } =
    useAsyncValidationForm<PipelineSettingsAsyncFields>();

  const { mutate, mutationState, error } = useMutation({
    mutationTarget$: args =>
      PipelineAPI.editIntegrationConfig(pipeline.id, args as PipelineConfigPayload),
    onMutationSuccess: res => {
      eventTrack({
        action: PipelineSettingsTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_SAVE_SUCCESS,
        properties: {
          pipelineId: pipeline.id
        }
      });

      onSavePipeline(res.data);
    },
    onMutationError: e => {
      scrollToBottom();

      eventTrack({
        action: PipelineSettingsTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_SAVE_FAILURE,
        properties: {
          pipelineId: pipeline.id,
          error: e
        }
      });
    }
  });

  const savePipelineConfig = (formValues: unknown) => {
    eventTrack({
      action: PipelineSettingsTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_SAVE,
      properties: {
        pipelineId: pipeline.id
      }
    });

    const pipelineConfig = PipelineSettingsConfigHelper.buildConfigParams(
      formValues as PipelineSettingsFormState,
      pipeline
    );

    mutate(pipelineConfig);
  };

  const onValidationError = (errors: unknown) => {
    eventTrack({
      action: PipelineSettingsTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_VALIDATION_FAILURE,
      properties: {
        pipelineId: pipeline.id,
        errors
      }
    });
  };

  const submitForm = () => {
    eventTrack({
      action: PipelineSettingsTrackers.PIPELINE_TRACKER_PIPELINE_SETTINGS_SAVE_CLICK,
      properties: {
        pipelineId: pipeline.id
      }
    });

    runValidationsAndSubmit(
      formikRef.current as FormikProps<unknown>,
      savePipelineConfig,
      onValidationError
    );
  };

  const isEditEnabled = isEditPipelineConfigAllowed();
  const isFormInteractionBlocked = mutationState === MutationState.MUTATING || isValidating;

  const disableNonEditableFields = isEditing || isFormInteractionBlocked;
  const disableEditableFields = (isEditing && !isEditEnabled) || isFormInteractionBlocked;

  return {
    submitForm,
    isValidating,
    mutationState,
    error,
    validationSchema,
    initialFormValues,
    isFormInteractionBlocked,
    disableNonEditableFields,
    disableEditableFields
  };
}
